/**
 * mock.ts — Shared UI types + static display config.
 *
 * Historical note: this file held the design-prototype fixture data the shell
 * shipped with. All fixtures were removed in the 2026-07-24 de-fake pass
 * (every surface now renders real backend data or an explicit "In arrivo"
 * state). What remains is NOT mock data:
 *   - render-contract types (DashboardStat, UploadFile, the Kb* wire types)
 *   - MOCK_KB_DOMAINS — the static domain display config (label/color/icon
 *     per KB domain key), which is configuration, not fixture data.
 * The filename is kept to avoid a noisy rename across ~10 importers.
 */

export interface DashboardStat {
  label: string;
  value: string;
  delta: string;
  trend?: "up" | "down";
  warn?: boolean;
}

export type UploadFileType = "pdf" | "xlsx" | "dwg" | "other";

export interface UploadFile {
  name: string;
  type: UploadFileType;
  size: string;
  note?: string;
}

// ─── Knowledge Base types (wire/render contracts for kbApi + KB pages) ───────

export type KbDocStatus =
  | "live"
  | "pending-review"
  | "rejected"
  | "ingesting"
  | "archived"
  | "pending-manual-merge";

export interface KbDoc {
  id: string;
  filename: string;
  domain: string;
  uploader: string;
  date: string;
  chunks: number;
  status: KbDocStatus;
  /** live synthesis stage while status === "ingesting" (drives the progress UI) */
  synthesisStage?: string | null;
  generatedPageIds?: string[];
}

export interface KbDomain {
  key: string;
  label: string;
  color: string;
  /** key into the icon set `I` */
  icon: string;
}

/** Static domain display config (not fixture data): label/color/icon per key. */
export const MOCK_KB_DOMAINS: KbDomain[] = [
  { key: "HR", label: "HR", color: "#7C3AED", icon: "Suppliers" },
  { key: "IT", label: "IT", color: "#2563EB", icon: "Settings" },
  { key: "Procurement", label: "Procurement", color: "#B8730C", icon: "Euro" },
  { key: "Product", label: "Product", color: "#16A34A", icon: "Box" },
  { key: "Reference", label: "Reference", color: "#525866", icon: "File" },
  { key: "Altro", label: "Altro", color: "#8B5CF6", icon: "Folder" },
  { key: "Project", label: "Progetto", color: "#0EA5E9", icon: "Building" },
];

export type KbDecision = "accept_new" | "reject_new" | "merge" | "clarification";

export interface KbReview {
  id: string;
  newDocId: string;
  existingDocId: string | null;
  domain: string;
  uploader: string;
  uploadedAt: string;
  confidence: number;
  newSnippet: string;
  existingSnippet: string | null;
  existingDocSource: string | null;
  newDocSource: string;
  aiReasoning: string;
  aiRecommendation: KbDecision;
  decidedAt?: string;
  decidedBy?: string;
  decision?: KbDecision;
}

// ─── Esplora KB — synthesized wiki pages, edges, lint ────────────────────────

export type WikiPageType = "concept" | "entity" | "source" | "analysis";

export interface KbWikiPage {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string;
  chunks: number;
  sourceFileIds: string[];
  pageRefs: string[];
  status?: KbDocStatus;
}

export interface KbWikiEdge {
  from: string;
  to: string;
  type: "ref" | "sim";
}

export type LintCategory =
  | "contradiction"
  | "stale"
  | "orphan"
  | "missing-concept"
  | "missing-link"
  | "data-gap";

export interface KbLintFinding {
  id: string;
  category: LintCategory;
  severity: "warn" | "info";
  title: string;
  summary: string;
  suggestion: string;
  isNew?: boolean;
  pageA?: string;
  pageB?: string;
  linkedReviewId?: string;
}
